import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get, isEmpty, isEqual, isUndefined, isNull } from 'lodash';
import qs from 'qs';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import asyncRoute from '../../../../../helpers/hoc/asyncRoute';
import rewardsRoutes from '../../../../../routes/rewards';
import {
  fetchDealByIdAction,
  clearBrandDealDataAction,
  fetchRewardGuestDetailsAction,
  fetchRewardPointsAction,
} from '../../../../../actions/rewards';
import {
  clearSubscribedGuestDealAction,
  fetchSubscribedGuestDealAction,
  subscribeDealAction,
} from '../../../../../actions/app.action';

import resourcesConfig from '../../../../../resources';
import { apiResponseCodes, dealTypes } from '../../../../../constants/globals';
import { constants, webAppDomain } from '../../../../../constants/configuration';
import {
  birthdayRewardAvailableDate,
  anniversaryRewardAvailableDate,
} from '../../../../Rewards/helpers';
import { toTitleCase, renderCount } from '../../../../../utils/helpers';
import { formatDate } from '../../../../../utils/fudrLocalizationHelper';
import {
  birthdayRewardPreMonths,
  rewardCategory,
  anniversaryrewardPreMonths,
} from '../../../../Rewards/config';
import orderingRoutes from '../../../../../routes/ordering';

import CouponCardSkeleton from '../../../../Rewards/components/Skeleton/CouponCardSkeleton';
import LocationCardSkeleton from '../../../../Rewards/components/Skeleton/LocationCardSkeleton';
import SadEmojiIcon from '../../../../../assets/images/sad-emoji.png';
import celebrationBanner from '../../../../../assets/images/celebration-banner.png';

import './CampaignDetail.scss';

const RewardCard = asyncRoute(() => import('../../../../Rewards/components/Card/RewardCard'));
const CouponTnC = asyncRoute(() => import('../../../../../components/CouponTnC'));
const HowToReedem = asyncRoute(() => import('../../../../Rewards/components/HowToReedem'));
const RedemptionDetails = asyncRoute(() =>
  import('../../../../../components/Popup/RedemptionDetails'),
);
const Icons = asyncRoute(() => import('../../../../../components/Icons'));
const Popup = asyncRoute(() => import('../../../../../components/Popup'));
const SlideAnimation = asyncRoute(() => import('../../../../Rewards/components/SlideAnimation'));
const Button = asyncRoute(() => import('../../../../../components/MaterialElements/Button'));

const CampaignDetail = ({
  actions,
  location,
  history,
  weekdays,
  rewardPoints,
  guestId,
  guestDetails,
  brandGuestId,
  brandId,
  rewardProgram,
  socialMediaCampaignDetails,
  brandName,
  fallbackBrandName,
  brandDeal,
  subscribedDeal,
  enableCampaignDealAutoSubscribe,
}) => {
  const intl = useIntl();

  const dob = get(guestDetails, 'dob', '');
  const doa = get(guestDetails, 'doa', '');
  const birthdayDealStartDate =
    dob && birthdayRewardAvailableDate(dob, birthdayRewardPreMonths, 'claim').startDate;
  const birthdayDealEndDate = dob && birthdayRewardAvailableDate(dob).endDate;
  const anniversaryDealStartDate =
    doa && anniversaryRewardAvailableDate(doa, anniversaryrewardPreMonths, 'claim').startDate;
  const anniversaryDealEndDate = doa && anniversaryRewardAvailableDate(doa).endDate;
  const [deal, setDeal] = useState({});
  const [parentDeal, setParentDeal] = useState({});
  const [animationType, setAnimationType] = useState();
  const [rewardClaimed, setRewardClaimed] = useState(false);
  const [initialAPIInteractionDone, setInitialAPIInteractionDone] = useState(false);
  const [alreadyRedeemedDealDetails, setAlreadyRedeemedDealDetails] = useState({});
  const [isSwipeButtonReset, setIsSwipeButtonReset] = useState(false);
  const [showNoAvailableOfferSection, setShowNoAvailableOfferSection] = useState(false);
  const [showGreeterSection, setShowGreeterSection] = useState(false);
  const [campaingRewardCTA, setCampaignRewardCTA] = useState('');
  const [offerRedemptionSource, setOfferRedemptionSource] = useState('');

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const isExistingUser = get(queryParams, 'isExistingUser', '') === 'true';
  const dealTitle = get(queryParams, 'dealTitle', '');
  const campaignCode = get(queryParams, 'campaignCode', '');
  const userId = get(queryParams, 'userId', '');
  const userBusinessId = get(queryParams, 'userBusinessId', '');

  useEffect(() => {
    setAnimationType(get(location, 'state.animationType', 'fadeInOut'));
    return () => {
      actions.clearBrandDealData();
      actions.clearSubscribedDeal();
    };
  }, []);

  useEffect(() => {
    const campaignPlan = get(socialMediaCampaignDetails, 'campaignPlan', {});
    const defaultCampaignRewardCTA = intl.formatMessage({
      id: 'orderNowCTA',
      defaultMessage: resourcesConfig.orderNowCTA,
    });
    const campaingRewardCTA = get(
      campaignPlan,
      'additionalData.campaingRewardCTA',
      defaultCampaignRewardCTA,
    );
    const offerRedemptionSource = get(campaignPlan, 'additionalData.offerRedemptionSource', '');
    const dealCode = get(campaignPlan, 'rules.campaignDeals', []).find(() => true);
    const fetchDealData = async () => {
      try {
        const existingUserContext = get(campaignPlan, 'existingUser', {});
        const existingUserOffer = getOfferForExistingUser({
          existingUserContext,
        });
        if (isEmpty(existingUserOffer) && !get(existingUserOffer, 'hasAlternateOffer')) {
          const campaignDealResponse = await actions.fetchDealById({ dealCode, guestId });
          if (apiResponseCodes.SUCCESS.includes(get(campaignDealResponse, 'status', ''))) {
            const guestCoupons = get(campaignDealResponse, 'data.guestCoupons', []);
            const guestCoupon = guestCoupons.find((gCoupon) => gCoupon) || {};
            const activeDealGuests = get(guestCoupon, 'activeDealGuests.0', {});
            const redeemedDealGuest = get(guestCoupon, 'redeemedDealGuests.0', {});
            const expiredDealGuest = get(guestCoupon, 'expiredDealGuests.0', {});
            const perGuestMax = get(campaignDealResponse, 'data.perGuestMax');
            const dealData = get(campaignDealResponse, 'data');

            // For STREAK deals, store parent deal and handle active coupon
            if (isEqual(get(dealData, 'dealType'), dealTypes.streak)) {
              setParentDeal(dealData);
              if (!isEmpty(activeDealGuests)) {
                setShowGreeterSection(false);
                let activeCouponId = get(activeDealGuests, 'activeDealGuestId');
                let activeCoupon = get(activeDealGuests, 'subDealGuests', []).find(
                  (coupon) => coupon.id === activeCouponId,
                );
                setDeal(activeCoupon || activeDealGuests);
              } else {
                setShowGreeterSection(true);
                setDeal(dealData);
                const dealId = get(dealData, 'id', '');
                if (!isEmpty(dealId) && enableCampaignDealAutoSubscribe) {
                  handleDealCTAClick(dealId);
                }
              }
            } else {
              if (!isEmpty(activeDealGuests)) {
                setShowGreeterSection(false);
                setDeal(activeDealGuests);
              } else if (
                isNull(perGuestMax) ||
                isUndefined(perGuestMax) ||
                perGuestMax > redeemedDealGuest.length
              ) {
                setShowGreeterSection(true);
                setDeal(dealData);
                const dealId = get(dealData, 'id', '');
                if (!isEmpty(dealId) && enableCampaignDealAutoSubscribe) {
                  handleDealCTAClick(dealId);
                }
              } else if (!isEmpty(redeemedDealGuest)) {
                setShowGreeterSection(false);
                setDeal(redeemedDealGuest);
              } else if (!isEmpty(expiredDealGuest)) {
                setShowGreeterSection(false);
                setDeal(expiredDealGuest);
              } else {
                setShowGreeterSection(true);
                setDeal(dealData);
                const dealId = get(dealData, 'id', '');
                if (!isEmpty(dealId) && enableCampaignDealAutoSubscribe) {
                  handleDealCTAClick(dealId);
                }
              }
            }
          }
        } else {
          const hasAlternateOffer = get(existingUserOffer, 'hasAlternateOffer');
          if (!hasAlternateOffer) {
            setShowNoAvailableOfferSection(true);
          } else {
            const fallbackDealCode = get(existingUserOffer, 'fallbackDeals', []).find(() => true);
            const response = await actions.fetchDealById({
              dealCode: fallbackDealCode,
              guestId,
            });
            if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
              const fallbackDealGuestCoupons = get(response, 'data.guestCoupons', []);
              const fallbackDealGuestCoupon =
                fallbackDealGuestCoupons.find((gCoupon) => gCoupon) || {};
              const falbackDealActiveDealGuests = get(
                fallbackDealGuestCoupon,
                'activeDealGuests.0',
                {},
              );
              const falbackDealRedeemedDealGuest = get(
                fallbackDealGuestCoupon,
                'redeemedDealGuests.0',
                {},
              );
              const falbackDealExpiredDealGuest = get(
                fallbackDealGuestCoupon,
                'expiredDealGuests.0',
                {},
              );
              const falbackDealPerGuestMax = get(response, 'data.perGuestMax');
              const fallbackDealData = get(response, 'data');

              // For fallback STREAK deals, store parent deal and handle active coupon
              if (isEqual(get(fallbackDealData, 'dealType'), dealTypes.streak)) {
                setParentDeal(fallbackDealData);
                if (!isEmpty(falbackDealActiveDealGuests)) {
                  setShowGreeterSection(false);
                  let activeCouponId = get(falbackDealActiveDealGuests, 'activeDealGuestId');
                  let activeCoupon = get(falbackDealActiveDealGuests, 'subDealGuests', []).find(
                    (coupon) => coupon.id === activeCouponId,
                  );
                  setDeal(activeCoupon || falbackDealActiveDealGuests);
                } else {
                  setShowGreeterSection(true);
                  setDeal(fallbackDealData);
                  const dealId = get(fallbackDealData, 'id', '');
                  if (!isEmpty(dealId) && enableCampaignDealAutoSubscribe) {
                    handleDealCTAClick(dealId);
                  }
                }
              } else {
                if (!isEmpty(falbackDealActiveDealGuests)) {
                  setShowGreeterSection(false);
                  setDeal(falbackDealActiveDealGuests);
                } else if (
                  isNull(falbackDealPerGuestMax) ||
                  isUndefined(falbackDealPerGuestMax) ||
                  falbackDealPerGuestMax > falbackDealRedeemedDealGuest.length
                ) {
                  setShowGreeterSection(true);
                  setDeal(fallbackDealData);
                  const dealId = get(fallbackDealData, 'id', '');
                  if (!isEmpty(dealId) && enableCampaignDealAutoSubscribe) {
                    handleDealCTAClick(dealId);
                  }
                } else if (!isEmpty(falbackDealRedeemedDealGuest)) {
                  setShowGreeterSection(false);
                  setDeal(falbackDealRedeemedDealGuest);
                } else if (!isEmpty(falbackDealExpiredDealGuest)) {
                  setShowGreeterSection(false);
                  setDeal(falbackDealExpiredDealGuest);
                } else {
                  setShowGreeterSection(true);
                  setDeal(fallbackDealData);
                  const dealId = get(fallbackDealData, 'id', '');
                  if (!isEmpty(dealId) && enableCampaignDealAutoSubscribe) {
                    handleDealCTAClick(dealId);
                  }
                }
              }
            }
          }
        }

        setInitialAPIInteractionDone(true);
      } catch (error) {
        console.error('Error fetching deal data:', error);
      }
    };
    if (!isEmpty(dealCode)) {
      fetchDealData();
      setCampaignRewardCTA(campaingRewardCTA);
      setOfferRedemptionSource(offerRedemptionSource);
    }
  }, [socialMediaCampaignDetails]);

  useEffect(() => {
    checkDealClaimed(brandDeal, subscribedDeal);
  }, [deal]);

  useEffect(() => {
    if (isEmpty(rewardPoints)) {
      if (!isEmpty(brandId) && !isEmpty(brandGuestId)) {
        actions.fetchRewardPoints({
          url: `/aggregateRecords/guests?brandId=${brandId}&entityType=BRAND_GUEST&entityId=${brandGuestId}&recordTypes=REWARD_POINT&frequencyTypes=ALL_TIME`,
        });
      }
    }
  }, [brandGuestId]);

  const checkDealClaimed = (brandDeal, subscribedDeal) => {
    let claimed =
      isEmpty(get(deal, 'subscribeTime', '')) &&
      isEqual(get(brandDeal, 'id'), get(subscribedDeal, 'dealId', ''));
    if (claimed) {
      if (isEqual(get(subscribedDeal, 'dealType'), dealTypes.streak)) {
        let activeCouponId = get(subscribedDeal, 'activeDealGuestId');
        let activeCoupon = get(subscribedDeal, 'subDealGuests', []).find(
          (coupon) => coupon.id === activeCouponId,
        );
        setDeal(activeCoupon);
      } else {
        setDeal(subscribedDeal);
      }
    }
  };

  const getOfferForExistingUser = ({ existingUserContext }) => {
    // Check if existingUserContext object exists
    if (!isEmpty(existingUserContext)) {
      if (isExistingUser) {
        const { hasAlternateOffer } = existingUserContext;
        if (hasAlternateOffer) {
          return existingUserContext;
        } else {
          return { hasAlternateOffer };
        }
      } else {
        return {};
      }
    } else {
      return {};
    }
  };

  const checkCouponStatus = (coupon) => {
    if (!isEmpty(get(coupon, 'redeemTime', ''))) {
      return constants.couponDetail.couponStatus.redeemed;
    } else if (get(coupon, 'isExpired', '') === true) {
      return constants.couponDetail.couponStatus.expired;
    } else if (get(coupon, 'subscribeTime', '')) {
      return constants.couponDetail.couponStatus.subscribed;
    } else {
      return false;
    }
  };

  const handleDealCTAClick = (dealId) => {
    const dealCategory = get(deal, 'category', '');
    const guestCoupons = get(deal, 'guestCoupons', []);
    const perGuestMax = get(deal, 'perGuestMax');
    const guestCoupon = (guestCoupons.length > 0 && guestCoupons.find((gCoupon) => gCoupon)) || {};
    const redeemedDealGuest = get(guestCoupon, 'redeemedDealGuests', []);
    const additionalCampaignBody = {
      sourceCode: campaignCode,
      sourceId: get(socialMediaCampaignDetails, 'id', ''),
      source: 'CAMPAIGN',
    };
    const subscribeDealReqBody = {
      dealId,
      dealGuest: {
        guestId,
        brandGuestId,
        ...(!isEmpty(additionalCampaignBody) && additionalCampaignBody),
      },
      socialCampaignCode: campaignCode,
      externalUser: {
        type: 'IG',
        userId: userId,
        userBusinessId: userBusinessId,
      },
      communicaiton: {
        isSetReminder: true,
        isSendUserAck: true,
        isSendBusinessAck: true,
        isSendExternalUserAck: true,
      },
    };
    setIsSwipeButtonReset(false);
    const withLoader = false;
    if (dealCategory === rewardCategory.reward) {
      actions.subscribeDeal({ subscribeDealReqBody }, withLoader).then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
          setRewardClaimed(true);
          if (isEqual(get(response, 'data.dealGuest.dealType'), dealTypes.streak)) {
            let activeCouponId = get(response, 'data.dealGuest.activeDealGuestId');
            let activeCoupon = get(response, 'data.dealGuest.subDealGuests', []).find(
              (coupon) => coupon.id === activeCouponId,
            );
            actions.fetchDealById({ dealCode: deal.code, guestId }).then((response) => {
              setParentDeal(response.data);
            });
            setDeal(activeCoupon);
          } else {
            setDeal(get(response, 'data.dealGuest'));
          }
        } else if (isEqual(get(response, 'data.code'), 'DEAL_ALREADY_ASSIGNED')) {
          setAlreadyRedeemedDealDetails(get(response, 'data.data.dealGuestErrors', {}));
          setIsSwipeButtonReset(true);
        }
      });
    } else {
      if (
        isNull(perGuestMax) ||
        isUndefined(perGuestMax) ||
        perGuestMax > redeemedDealGuest.length
      ) {
        actions.subscribeDeal({ subscribeDealReqBody }).then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
            setRewardClaimed(true);
            if (isEqual(get(response, 'data.dealGuest.dealType'), dealTypes.streak)) {
              let activeCouponId = get(response, 'data.dealGuest.activeDealGuestId');
              let activeCoupon = get(response, 'data.dealGuest.subDealGuests', []).find(
                (coupon) => coupon.id === activeCouponId,
              );
              setDeal(activeCoupon);
            } else {
              setDeal(get(response, 'data.dealGuest'));
            }
          } else if (isEqual(get(response, 'data.code'), 'DEAL_ALREADY_ASSIGNED')) {
            setAlreadyRedeemedDealDetails(get(response, 'data.data.dealGuestErrors', {}));
            setIsSwipeButtonReset(true);
          }
        });
      }
    }
  };

  const renderDealAlreadyRedeemedPopupContent = (details) => {
    let redeemedDeal = {};
    if (details.length === 1) {
      redeemedDeal = get(details, '0', {});
    }
    let title = '';
    let description = '';
    let guestName = get(redeemedDeal, 'guestName', get(redeemedDeal, 'dealGuests.0.guestName', ''));
    let redeemTime = get(
      redeemedDeal,
      'redeemTime',
      get(redeemedDeal, 'dealGuests.0.redeemTime', ''),
    );
    let discountAmount = get(
      redeemedDeal,
      'discountAmount',
      get(redeemedDeal, 'dealGuests.0.discountAmount', 0),
    );
    let freeItemCount = get(
      redeemedDeal,
      'freeItemCount',
      get(redeemedDeal, 'dealGuests.0.freeItemCount', 0),
    );
    const subscribedDate = get(redeemedDeal, 'dealGuests.0.subscribeTime', '');
    const isOnlySubscribed =
      !!get(redeemedDeal, 'alreadySubscribed') && !get(redeemedDeal, 'alreadyRedeemed');

    title = isOnlySubscribed ? (
      <FormattedMessage
        id="dealSubscribedPopupTitle"
        defaultMessage={resourcesConfig.dealSubscribedPopupTitle}
      />
    ) : (
      <FormattedMessage
        id="dealRedeemedPopupTitle"
        defaultMessage={resourcesConfig.dealRedeemedPopupTitle}
      />
    );

    if (isOnlySubscribed) {
      description = (
        <FormattedMessage
          id="dealAlreadySubscribedDescription"
          values={{
            username: toTitleCase(guestName),
            br: <br />,
            subscribedDate: <b>{formatDate(subscribedDate)}</b>,
          }}
          defaultMessage={resourcesConfig.dealAlreadySubscribedDescription}
        />
      );
    } else if (details.length > 1) {
      description = (
        <FormattedMessage
          id="offerAlreadyRedeemedMultipleTimes"
          defaultMessage={resourcesConfig.offerAlreadyRedeemedMultipleTimes}
        />
      );
    } else {
      if (freeItemCount > 0) {
        description = (
          <FormattedMessage
            id="dealAlreadyRedeemedItemFreeDescription"
            values={{
              username: toTitleCase(guestName),
              br: <br />,
              redeemDate: <b>{formatDate(redeemTime)}</b>,
            }}
            defaultMessage={resourcesConfig.dealAlreadyRedeemedItemFreeDescription}
          />
        );
      } else {
        description = (
          <FormattedMessage
            id="dealRedeemedPopupDescription"
            values={{
              username: toTitleCase(guestName),
              br: <br />,
              redeemDate: <b>{formatDate(redeemTime)}</b>,
              amount: <b>{`₹${discountAmount}`}</b>,
            }}
            defaultMessage={resourcesConfig.dealRedeemedPopupDescription}
          />
        );
      }
    }

    return { title, description };
  };

  const renderStreakItem = (subDeal, index) => {
    const { displayTitle, title, sequenceNumber } = subDeal;
    const guestCoupons = get(parentDeal, 'guestCoupons', []);
    const guestCoupon = guestCoupons.find((gCoupon) => gCoupon) || {};
    const subDealGuests = get(guestCoupon, 'activeDealGuests.0.subDealGuests', []);
    const activeDealGuestId = get(guestCoupon, 'activeDealGuests.0.activeDealGuestId', '');
    const dealGuest = subDealGuests.find((dealGuest) => isEqual(dealGuest.dealId, subDeal.id));

    const dealStatus = renderStreakItemStatus(dealGuest, activeDealGuestId);
    return (
      <div className="campaign-detail-v1_streak-item" key={index}>
        <div className="campaign-detail-v1_streak-item_number">
          <FormattedMessage
            id="streakItemSequenceTitle"
            defaultMessage={resourcesConfig.streakItemSequenceTitle}
            values={{ sequence: renderCount(sequenceNumber) }}
          />{' '}
          {isEmpty(dealStatus) && <Icons type="lockIcon" />}
        </div>
        <div
          className={classNames('campaign-detail-v1_streak-item_title', {
            active: isEqual(dealStatus, 'Active'),
          })}
        >
          {displayTitle ? displayTitle : title}
        </div>
        <div
          className={classNames('campaign-detail-v1_streak-item_status', {
            active: isEqual(dealStatus, 'Active'),
          })}
        >
          {dealStatus}
        </div>
      </div>
    );
  };

  const renderStreakItemStatus = (dealGuest, activeDealGuestId) => {
    if (isEqual(activeDealGuestId, get(dealGuest, 'id'))) {
      return 'Active';
    } else if (dealGuest && dealGuest.redeemTime) {
      return 'Redeemed';
    }
  };

  const onAreadyRedeemedPopupClose = () => {
    setIsSwipeButtonReset(true);
    setAlreadyRedeemedDealDetails({});
  };

  const couponStatus = checkCouponStatus(deal);
  const guestCoupons = get(deal, 'guestCoupons', []);
  const guestCoupon = guestCoupons.find((gCoupon) => gCoupon) || {};
  const subDealGuests = get(guestCoupon, 'activeDealGuests.0.subDealGuests', []);

  const showTnc =
    ![
      constants.couponDetail.couponStatus.expired,
      constants.couponDetail.couponStatus.redeemed,
    ].includes(couponStatus) && !showNoAvailableOfferSection;

  const handleGoBackClick = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('view', 'pre-signin'); // Set 'view' parameter
    searchParams.delete('isExistingUser'); // Delete 'isExistingUser' parameter
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const renderCampaignRewardTitle = () => {
    const campaignPlan = get(socialMediaCampaignDetails, 'campaignPlan', {});
    const existingUserContext = get(campaignPlan, 'existingUser', {});
    const existingUserOffer = getOfferForExistingUser({
      existingUserContext,
    });
    if (isEqual(couponStatus, constants.couponDetail.couponStatus.redeemed)) {
      return (
        <FormattedMessage
          id="offerAlreadyRedeemed"
          defaultMessage={resourcesConfig.offerAlreadyRedeemed}
        />
      );
    }
    if (isEqual(couponStatus, constants.couponDetail.couponStatus.expired)) {
      return (
        <FormattedMessage
          id="offerExpiredTitle"
          defaultMessage={resourcesConfig.offerExpiredTitle}
        />
      );
    } else if (!isEmpty(existingUserOffer)) {
      if (get(existingUserOffer, 'hasAlternateOffer')) {
        return (
          <FormattedMessage
            id="unlockFallbackOfferTitle"
            defaultMessage={resourcesConfig.unlockFallbackOfferTitle}
            values={{
              fallbackDisplayTitle: get(deal, 'displayTitle', ''),
              displayTitle: dealTitle,
            }}
          />
        );
      } else {
        return '';
      }
    } else {
      return (
        <FormattedMessage
          id="unlockOfferTitle"
          defaultMessage={resourcesConfig.unlockOfferTitle}
          values={{ br: <br />, displayTitle: dealTitle }}
        />
      );
    }
  };

  const onGetBrandAppCTAClick = () => {
    // Define your target path and fallback URLs.
    const targetPath = `${rewardsRoutes.rewards}/${orderingRoutes.ordering}`; // intended deep link path (without leading slash)
    const universalLink = `${window.location.origin}/${targetPath}`; // fallback target if needed

    // For non-Android or non-Instagram browsers, just use your fallback redirection URL.
    const redirectionUrl = `https://${webAppDomain}/redirect?target=${encodeURIComponent(
      universalLink,
    )}`;
    const anchor = document.createElement('a');
    anchor.href = redirectionUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  };

  const celebrationBannerUrl = get(
    socialMediaCampaignDetails,
    'campaignPlan.additionalData.celebrationBannerUrl',
    celebrationBanner,
  );

  return (
    <SlideAnimation animationType={animationType}>
      <div className="campaign-detail-v1">
        <CouponCardSkeleton
          isLoading={!initialAPIInteractionDone}
          variant="swipe"
          numberOfElements={1}
        >
          {showGreeterSection && (
            <div className="campaign-detail-v1_banner">
              <img src={celebrationBannerUrl} alt="celebrationBanner"></img>
            </div>
          )}

          <div className="campaign-detail-v1_title">{renderCampaignRewardTitle()}</div>

          {!showNoAvailableOfferSection && (
            <RewardCard
              currencyName={get(rewardProgram, 'currencyName', resourcesConfig.rewardPoints)}
              rewardPoints={rewardPoints}
              deal={deal}
              onDealCTAClick={(dealId) => handleDealCTAClick(dealId)}
              dob={dob}
              doa={doa}
              birthdayDealStartDate={birthdayDealStartDate}
              birthdayDealEndDate={birthdayDealEndDate}
              anniversaryDealStartDate={anniversaryDealStartDate}
              anniversaryDealEndDate={anniversaryDealEndDate}
              history={history}
              rewardClaimed={rewardClaimed}
              isSwipeButtonReset={isSwipeButtonReset}
              showOrderRedirectionCTA={true}
              campaingRewardCTA={campaingRewardCTA}
              offerRedemptionSource={offerRedemptionSource}
            />
          )}

          {!isEmpty(parentDeal) ? (
            <div className="campaign-detail-v1_streak-deals">
              {get(parentDeal, 'subDeals', []).map(renderStreakItem)}
            </div>
          ) : (
            isEqual(deal.dealType, dealTypes.streak) && (
              <div className="campaign-detail-v1_streak-deals">
                {get(deal, 'subDeals', []).map(renderStreakItem)}
              </div>
            )
          )}

          {(isEqual(couponStatus, constants.couponDetail.couponStatus.redeemed) ||
            isEqual(couponStatus, constants.couponDetail.couponStatus.expired)) && (
            <div className="campaign-detail-v1_no-offer-available">
              <div className="campaign-detail-v1_no-offer-available_title">
                <FormattedMessage
                  id="getMoreOffers"
                  defaultMessage={resourcesConfig.getMoreOffers}
                />
              </div>
              <div className="campaign-detail-v1_no-offer-available_sub-description">
                <FormattedMessage
                  id="unlockMoreOfferOnApp"
                  defaultMessage={resourcesConfig.unlockMoreOfferOnApp}
                  values={{ br: <br /> }}
                />
              </div>
              <div className="campaign-detail-v1_no-offer-available_CTA">
                <Button
                  content={
                    <FormattedMessage
                      id="getBrandAppCTA"
                      defaultMessage={resourcesConfig.getBrandAppCTA}
                      values={{ brandName: fallbackBrandName ? fallbackBrandName : brandName }}
                    />
                  }
                  themeContext={rewardsRoutes.rewards}
                  onClick={() => onGetBrandAppCTAClick()}
                />
              </div>
            </div>
          )}
          {showNoAvailableOfferSection && (
            <div className="campaign-detail-v1_no-offer-available">
              <div className="campaign-detail-v1_no-offer-available_emoji">
                <img src={SadEmojiIcon} alt="no-offer-available" />
              </div>
              <div className="campaign-detail-v1_no-offer-available_title">
                <FormattedMessage
                  id="noOfferApplicableTitle"
                  defaultMessage={resourcesConfig.noOfferApplicableTitle}
                />
              </div>
              <div className="campaign-detail-v1_no-offer-available_description">
                <FormattedMessage
                  id="noOfferApplicableDescription"
                  defaultMessage={resourcesConfig.noOfferApplicableDescription}
                />
              </div>
              <div className="campaign-detail-v1_no-offer-available_CTA">
                <Button
                  content={
                    <FormattedMessage
                      id="getBrandAppCTA"
                      defaultMessage={resourcesConfig.getBrandAppCTA}
                      values={{ brandName }}
                    />
                  }
                  themeContext={rewardsRoutes.rewards}
                  onClick={() => onGetBrandAppCTAClick()}
                />
              </div>
            </div>
          )}
        </CouponCardSkeleton>

        {!initialAPIInteractionDone ? (
          <>
            <LocationCardSkeleton isLoading={!initialAPIInteractionDone} lines="4" />
            <div className="campaign-detail-v1_section-break" />
            <LocationCardSkeleton isLoading={!initialAPIInteractionDone} lines="4" />
            <div className="campaign-detail-v1_section-break" />
            <LocationCardSkeleton isLoading={!initialAPIInteractionDone} lines="4" />
          </>
        ) : (
          showTnc && (
            <>
              <HowToReedem
                applicableOn={get(deal, 'applicableOn', 'ALL')}
                view={get(deal, 'category')}
                deal={deal}
              />
              <div className="campaign-detail-v2_section-break" />
              <CouponTnC
                deal={!isEmpty(parentDeal) ? parentDeal : deal}
                subDealGuests={subDealGuests}
                weekdays={weekdays}
                dob={dob}
                doa={doa}
              />
            </>
          )
        )}
        {isEqual(couponStatus, constants.couponDetail.couponStatus.redeemed) && (
          <RedemptionDetails
            subscribedDeal={deal}
            hiddenFields={['name', 'phoneNumber', 'discount', 'billAmount']}
          />
        )}
      </div>
      {!isEmpty(alreadyRedeemedDealDetails) && (
        <Popup
          openSegmentPopup={!isEmpty(alreadyRedeemedDealDetails)}
          content={renderDealAlreadyRedeemedPopupContent(alreadyRedeemedDealDetails)}
          dismissSegmentPopup={() => onAreadyRedeemedPopupClose()}
        />
      )}
    </SlideAnimation>
  );
};

const mapStateToProps = (state) => ({
  brandId: get(state, 'common.appStartupInfo.brandId', ''),
  brandName: get(state, 'common.appStartupInfo.brand.name', ''),
  fallbackBrandName: get(state, 'common.resources.brandName', ''),
  rewardProgram: get(state, 'common.appStartupInfo.rewardProgram', {}),
  brandDetails: get(state, 'common.appStartupInfo.brand', ''),
  weekdays: get(state, 'app.masterData.weekdays', []),
  rewardPoints: get(state, 'rewards.rewardPoints._embedded.guestAggregateRecords.0.value', 0),
  guestId: get(state, 'rewards.rewardGuestDetails.guestId', ''),
  brandGuestId: get(state, 'rewards.rewardGuestDetails.id', ''),
  guestDetails: get(state, 'rewards.rewardGuestDetails', {}),
  brandDeal: get(state, 'rewards.brandDealDetail', {}),
  subscribedDeal: get(state, 'app.subscribedGuestDeal', {}),
  socialMediaCampaignDetails: get(state, 'common.socialMediaCampaignDetails', {}),
  enableCampaignDealAutoSubscribe: get(
    state,
    'common.resources.enableCampaignDealAutoSubscribe',
    true,
  ),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchDealById: fetchDealByIdAction,
      clearBrandDealData: clearBrandDealDataAction,
      subscribeDeal: subscribeDealAction,
      fetchSubscribedGuestDeal: fetchSubscribedGuestDealAction,
      fetchRewardGuestDetails: fetchRewardGuestDetailsAction,
      fetchRewardPoints: fetchRewardPointsAction,
      clearSubscribedDeal: clearSubscribedGuestDealAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignDetail);
